import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, message, Modal, Select, Tag, Typography } from 'antd';
import styled from 'styled-components/macro';

import { CorpUser, Entity, EntityType, OwnerEntityType } from '../../../../../../../types.generated';
import { useTranslation } from '../../../../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../../../../useEntityRegistry';

import analytics, { EventType, EntityActionType } from '../../../../../../analytics';
import {
    useBatchAddOwnersMutation,
    useBatchRemoveOwnersMutation,
} from '../../../../../../../graphql/mutations.generated';
import { useGetSearchResultsLazyQuery } from '../../../../../../../graphql/search.generated';
import { useGetRecommendations } from '../../../../../../shared/recommendation';
import { OwnerLabel } from '../../../../../../shared/OwnerLabel';
import { handleBatchError } from '../../../../utils';
import { useListOwnershipTypesQuery } from '../../../../../../../graphql/ownership.generated';

import { OWNERSHIP_DISPLAY_TYPES } from './ownershipUtils';
import { DefaultValueType } from '../../../../../../shared/tags/DefaultValueType';
import { USER_SEARCH_QUERY_FILTER } from '../../../../../../search/filters/searchFilterConstantsService';

const SelectInput = styled(Select)`
    width: 480px;
`;

export enum OperationType {
    ADD,
    REMOVE,
    EDIT,
}

type Props = {
    urns: string[];
    defaultOwnerType?: string;
    hideOwnerType?: boolean | undefined;
    operationType?: OperationType;
    onCloseModal: () => void;
    refetch?: () => Promise<any>;
    entityType?: EntityType; // Only used for tracking events
    onOkOverride?: (result: SelectedOwner[]) => void;
    title?: string;
    defaultValues?: DefaultValueType[];
};

type SelectedOwner = {
    label: string | React.ReactNode;
    value: {
        ownerUrn: string;
        ownerEntityType: EntityType;
    };
};

export const EditOwnersModal = ({
    urns,
    hideOwnerType,
    defaultOwnerType,
    operationType = OperationType.ADD,
    onCloseModal,
    refetch,
    entityType,
    onOkOverride,
    title,
    defaultValues,
}: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();

    // Renders a search result in the select dropdown.
    const renderSearchResult = (entity: Entity) => {
        const avatarUrl =
            (entity.type === EntityType.CorpUser && (entity as CorpUser).editableProperties?.pictureLink) || undefined;
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return (
            <Select.Option value={entity.urn} key={entity.urn} tabIndex={0} aria-label={displayName}>
                <OwnerLabel name={displayName} avatarUrl={avatarUrl} type={entity.type} />
            </Select.Option>
        );
    };

    const renderDropdownResult = (entity: Entity) => {
        const avatarUrl =
            entity.type === EntityType.CorpUser
                ? (entity as CorpUser).editableProperties?.pictureLink || undefined
                : undefined;
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return <OwnerLabel name={displayName} avatarUrl={avatarUrl} type={entity.type} />;
    };

    const defaultValuesToSelectedOwners = (vals: { urn: string; entity?: Entity | null }[]): SelectedOwner[] => {
        return vals.map((defaultValue) => ({
            label: defaultValue.entity ? renderDropdownResult(defaultValue.entity) : defaultValue.urn,
            value: {
                ownerUrn: defaultValue.urn,
                ownerEntityType: defaultValue.entity?.type || EntityType.CorpUser,
            },
        }));
    };

    const [inputValue, setInputValue] = useState('');
    const [batchAddOwnersMutation] = useBatchAddOwnersMutation();
    const [batchRemoveOwnersMutation] = useBatchRemoveOwnersMutation();
    const { data: ownershipTypesData, loading } = useListOwnershipTypesQuery({
        variables: {
            input: {},
        },
    });
    const ownershipTypes = useMemo(() => {
        const typesData = ownershipTypesData?.listOwnershipTypes?.ownershipTypes || [];
        return [...typesData, ...OWNERSHIP_DISPLAY_TYPES];
    }, [ownershipTypesData]);

    const [selectedOwners, setSelectedOwners] = useState<SelectedOwner[]>(
        defaultValuesToSelectedOwners(defaultValues || []),
    );
    const [selectedOwnerType, setSelectedOwnerType] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (ownershipTypes.length) {
            const defaultType = ownershipTypes.find((type) => type.urn === defaultOwnerType);
            setSelectedOwnerType(defaultType?.urn || ownershipTypes[0].urn);
        }
    }, [ownershipTypes, defaultOwnerType]);

    // TODO: @momin commenting the GROUP implementation for now. (User and group dropdown search results!)
    // User dropdown search results!
    const [userSearch, { data: userSearchData }] = useGetSearchResultsLazyQuery();
    // const [groupSearch, { data: groupSearchData }] = useGetSearchResultsLazyQuery();
    const userSearchResults = userSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    // const groupSearchResults = groupSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    const combinedSearchResults = userSearchResults;
    // const combinedSearchResults = [...userSearchResults, ...groupSearchResults];

    // EntityType.CorpGroup is not been added in useGetRecommendations as currently we only need to show USERS.
    const [recommendedData] = useGetRecommendations([EntityType.CorpUser], USER_SEARCH_QUERY_FILTER);
    const inputEl = useRef(null);

    // Invokes the search API as the owner types
    const handleSearch = (type: EntityType, text: string, searchQuery: any) => {
        searchQuery({
            variables: {
                input: {
                    type,
                    query: text,
                    start: 0,
                    count: 5,
                    orFilters: USER_SEARCH_QUERY_FILTER,
                },
            },
        });
    };

    // Invokes the user search API for both users and groups.
    const handleActorSearch = (text: string) => {
        handleSearch(EntityType.CorpUser, text, userSearch);

        // TODO: @momin commented this for now, will remove the comment OR the code itself later
        // handleSearch(EntityType.CorpGroup, text, groupSearch);
    };

    const ownerResult = !inputValue || inputValue.length === 0 ? recommendedData : combinedSearchResults;

    const ownerSearchOptions = ownerResult?.map((result) => {
        return renderSearchResult(result);
    });

    const onModalClose = () => {
        setInputValue('');
        setSelectedOwners([]);
        setSelectedOwnerType(defaultOwnerType || undefined);
        onCloseModal();
    };

    /**
     * When a owner search result is selected, add the new owner  to the selectedOwners
     * value: {ownerUrn: string, ownerEntityType: EntityType}
     */
    const onSelectOwner = (selectedValue: { key: string; label: React.ReactNode; value: string }) => {
        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }
        const filteredActors = ownerResult
            ?.filter((entity) => entity.urn === selectedValue.value)
            .map((entity) => entity);
        if (filteredActors?.length) {
            const actor = filteredActors[0];
            const ownerEntityType =
                actor && actor.type === EntityType.CorpGroup ? OwnerEntityType.CorpGroup : OwnerEntityType.CorpUser;
            const newValues = [
                ...selectedOwners,
                {
                    label: selectedValue.value,
                    value: {
                        ownerUrn: selectedValue.value,
                        ownerEntityType: ownerEntityType as unknown as EntityType,
                    },
                },
            ];
            setSelectedOwners(newValues);
        }
    };

    // When a owner search result is deselected, remove the Owner
    const onDeselectOwner = (selectedValue: { key: string; label: React.ReactNode; value: string }) => {
        setInputValue('');
        const newValues = selectedOwners.filter(
            (owner) => owner.label !== selectedValue.value && owner.value.ownerUrn !== selectedValue.value,
        );
        setSelectedOwners(newValues);
    };

    // When a owner type is selected, set the type as selected type.
    const onSelectOwnerType = (urn: string) => {
        setSelectedOwnerType(urn);
    };

    const tagRender = ({ closable, label, onClose }: { closable: boolean; label: ReactNode; onClose: () => void }) => {
        return (
            <Tag
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                closable={closable}
                onClose={onClose}
            >
                {label}
            </Tag>
        );
    };

    const emitAnalytics = () => {
        if (urns.length > 1) {
            analytics.event({
                type: EventType.BatchEntityActionEvent,
                actionType: EntityActionType.UpdateOwnership,
                entityUrns: urns,
            });
        } else {
            analytics.event({
                type: EventType.EntityActionEvent,
                actionType: EntityActionType.UpdateOwnership,
                entityType,
                entityUrn: urns[0],
            });
        }
    };

    const batchAddOwners = async (inputs) => {
        try {
            await batchAddOwnersMutation({
                variables: {
                    input: {
                        owners: inputs,
                        resources: urns.map((urn) => ({ resourceUrn: urn })),
                    },
                },
            });
            message.success({ content: t('Owners Added'), duration: 2 });
            emitAnalytics();
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error(
                    handleBatchError(urns, e, {
                        content: t('Failed to add owners: {{message}}', { message: e.message || '' }),
                        duration: 3,
                    }),
                );
            }
        } finally {
            refetch?.();
            onModalClose();
        }
    };

    const batchRemoveOwners = async (inputs, runFinally = true) => {
        try {
            await batchRemoveOwnersMutation({
                variables: {
                    input: {
                        ownerUrns: inputs.map((input) => input.ownerUrn),
                        resources: urns.map((urn) => ({ resourceUrn: urn })),
                    },
                },
            });
            message.success({ content: t('Owners Removed'), duration: 2 });
            emitAnalytics();
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error(
                    handleBatchError(urns, e, {
                        content: t('Failed to remove owners: {{message}}', { message: e.message || '' }),
                        duration: 3,
                    }),
                );
            }
        } finally {
            if (runFinally) {
                refetch?.();
                onModalClose();
            }
        }
    };

    const areDefaultValuesSameAsSelectedOwners = (): boolean => {
        if (!defaultValues || defaultValues.length !== selectedOwners.length) {
            return false;
        }

        const defaultValuesMap = new Map(defaultValues.map((val) => [val.urn, val.entity?.type]));
        return selectedOwners.every(
            (owner) => defaultValuesMap.get(owner.value.ownerUrn) === owner.value.ownerEntityType,
        );
    };

    // Function to handle the modal action's
    const onOk = () => {
        if (selectedOwners.length === 0 && operationType !== OperationType.EDIT) {
            return;
        }

        if (onOkOverride) {
            onOkOverride(selectedOwners);
            return;
        }

        const inputs = selectedOwners.map((selectedActor) => {
            const lagacyOwnershipType = OWNERSHIP_DISPLAY_TYPES.filter((type) => type.urn === selectedOwnerType);

            const input = {
                ownerUrn: selectedActor.value.ownerUrn,
                ownerEntityType: selectedActor.value.ownerEntityType,
                ownershipTypeUrn: selectedOwnerType,
            };

            return lagacyOwnershipType.length
                ? {
                      ownerUrn: selectedActor.value.ownerUrn,
                      ownerEntityType: selectedActor.value.ownerEntityType,
                      type: lagacyOwnershipType[0]?.type,
                  }
                : input;
        });

        if (operationType === OperationType.ADD) {
            batchAddOwners(inputs);
        } else if (operationType === OperationType.EDIT) {
            if (!areDefaultValuesSameAsSelectedOwners() && defaultValues && defaultValues.length > 0) {
                const ownersToRemove = (defaultValues || [])
                    .filter(({ urn }) => !selectedOwners?.some(({ value }) => value.ownerUrn === urn))
                    .map(({ urn, entity }) => ({
                        ownerUrn: urn,
                        ownerEntityType: entity?.type,
                    }));

                const ownersToAdd = selectedOwners.filter(
                    (selectedOwner) =>
                        !defaultValues?.some((defaultValue) => defaultValue.urn === selectedOwner.value.ownerUrn),
                );

                if (ownersToRemove.length > 0) {
                    batchRemoveOwners(ownersToRemove, ownersToAdd.length === 0);
                }

                if (ownersToAdd.length > 0) {
                    batchAddOwners(
                        ownersToAdd.map((owner) => ({
                            ownerUrn: owner.value.ownerUrn,
                            ownerEntityType: owner.value.ownerEntityType,
                            ownershipTypeUrn: selectedOwnerType,
                        })),
                    );
                }
            }
        } else {
            batchRemoveOwners(inputs);
        }
    };

    function handleBlur() {
        setInputValue('');
    }
    let defaultTitle = t('Remove');
    if (operationType === OperationType.ADD) {
        defaultTitle = t('Add');
    } else if (operationType === OperationType.EDIT) {
        defaultTitle = t('Edit');
    }

    return (
        <Modal
            visible
            keyboard
            centered
            className="f-modal"
            onCancel={onModalClose}
            title={<h1>{title || `${defaultTitle} ${t('Owners')}`}</h1>}
            footer={
                <>
                    <Button
                        id="addOwnerButton"
                        disabled={selectedOwners.length === 0 && operationType !== OperationType.EDIT}
                        onClick={onOk}
                        type="primary"
                    >
                        {t('Save')}
                    </Button>
                    <Button onClick={onModalClose} type="default">
                        {t('Cancel')}
                    </Button>
                </>
            }
        >
            <Form layout="vertical" colon={false}>
                <Form.Item
                    key="owners"
                    name="owners"
                    className="mb-0"
                    label={<Typography.Text strong>{t('Owner')}</Typography.Text>}
                >
                    <Typography.Paragraph>{t('Find a user')}</Typography.Paragraph>
                    {/* <Typography.Paragraph>Find a user or group</Typography.Paragraph> */}
                    <Form.Item name="owner">
                        {/* placeholder="Search for users or groups..." */}
                        <SelectInput
                            autoFocus
                            showArrow
                            showSearch
                            size="large"
                            labelInValue
                            ref={inputEl}
                            mode="multiple"
                            filterOption={false}
                            aria-label={t('Select Owner')}
                            defaultActiveFirstOption={false}
                            placeholder={t('Search for users')}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                            onSelect={(asset: any) => onSelectOwner(asset)}
                            onDeselect={(asset: any) => onDeselectOwner(asset)}
                            onSearch={(value: string) => {
                                // eslint-disable-next-line react/prop-types
                                handleActorSearch(value.trim());
                                // eslint-disable-next-line react/prop-types
                                setInputValue(value.trim());
                            }}
                            tagRender={tagRender}
                            onBlur={handleBlur}
                            value={selectedOwners as any}
                            defaultValue={selectedOwners.map((owner) => ({
                                key: owner.value.ownerUrn,
                                value: owner.value.ownerUrn,
                                label: owner.label,
                            }))}
                        >
                            {ownerSearchOptions}
                        </SelectInput>
                    </Form.Item>
                </Form.Item>
                {!hideOwnerType && (
                    <Form.Item className="mb-0 pt-4" label={<Typography.Text strong>{t('Type')}</Typography.Text>}>
                        <Typography.Paragraph>{t('Choose an owner type')}</Typography.Paragraph>
                        <Form.Item name="type" className="mb-0">
                            {loading && <Select />}
                            {!loading && (
                                <Select
                                    size="large"
                                    value={selectedOwnerType}
                                    onChange={onSelectOwnerType}
                                    aria-label="Choose owner type"
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                                >
                                    {ownershipTypes.map((ownershipType) => {
                                        const ownershipTypeUrn = ownershipType?.urn || '';
                                        const ownershipTypeName = ownershipType?.info?.name || ownershipType?.urn || '';
                                        const ownershipTypeDescription = ownershipType?.info?.description || '';
                                        return (
                                            <Select.Option
                                                key={ownershipTypeUrn}
                                                value={ownershipTypeUrn}
                                                aria-label={ownershipTypeName}
                                                tabIndex={0}
                                            >
                                                <Typography.Text>{ownershipTypeName}</Typography.Text>
                                                <Typography.Paragraph
                                                    style={{ wordWrap: 'break-word', whiteSpace: 'break-spaces' }}
                                                    type="secondary"
                                                >
                                                    {ownershipTypeDescription}
                                                </Typography.Paragraph>
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            )}
                        </Form.Item>
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};
